/**
 * What the welcome call learned, in the product's own patient notes.
 *
 * Pete, 2026-09-22: *"put notes from welcome call into the product patient
 * notes"*. The availability report keeps the thing only it can draw — the days
 * and the window, as a grid — and the sentences a person wrote down about this
 * patient belong where a person would look for them, which is the notes field at
 * the top of the page the product already has.
 *
 * Caregiver, language and gender are the note-shaped constraints: they are
 * informational or they score, and none of them is a grid. SMS consent is gone
 * from the screen altogether. Availability days and windows stay out of here —
 * the grid says them better than a sentence can.
 */
import { useOrganization } from "~/providers/GraphqlProvider";
import { isHomeHealthDemoEnabled } from "../../demoEnabled";
import { useDemoState } from "../../store/useDemoStore";
import { patientRefForPage } from "./HomeHealthPatientSection";
import type { PatientConstraint } from "@caredna-marlin/utils/homeHealthDemo/state";

const NOTE_KINDS: PatientConstraint["kind"][] = ["caregiver", "language", "gender"];

/**
 * "Caregiver: Dialysis Wednesdays" — the value already reads as a sentence; the
 * kind names it. **No attribution**: who typed a note is not something the
 * product's own notes field carries, and a name in brackets after every line
 * made the demo's notes look unlike the product's (Pete, 2026-09-22).
 */
function noteLine(c: PatientConstraint): string {
  return c.kind === "caregiver" ? c.value : `${c.kind === "language" ? "Language" : "Clinician gender"}: ${c.value}`;
}

export function useHomeHealthPatientNotes(productNotes: string, patient?: { id?: number; firstName?: string | null; lastName?: string | null }): string {
  const { organizationName } = useOrganization();
  const state = useDemoState();
  if (!isHomeHealthDemoEnabled(organizationName)) return productNotes;
  const ref = patientRefForPage(state, patient?.id, patient);
  const notes = ref ? (state.patients[ref]?.constraints ?? []).filter((c) => NOTE_KINDS.includes(c.kind)).map(noteLine) : [];
  if (!notes.length) return productNotes;
  return [productNotes, ...notes].filter(Boolean).join("\n");
}
